import { Component, ComponentRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { delay, filter, finalize, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, NEVER, Observable, ReplaySubject } from 'rxjs';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { BatchImportRepository, IsfRepository } from '@data-layer/repositories';
import { CompaniesService } from '@services/companies.service';
import { BatchImportModel, PaginatedResultModel } from '@data-layer/models/common';
import { enumHeaderActionId } from '@app/modules/app-layout/enums';
import { IsfCountByStatusModel, IsfModel } from '@data-layer/models/isf';
import { SearchHelper } from '@helpers/search-helper';
import { IsfFilterType } from '../../models/isf-filter.type';
import { enumIsfStatusTab } from '../../enums/enumIsfStatusTab';
import { IHeaderAction } from '@app/modules/app-layout/interfaces';
import { enumIsfState } from '@data-layer/enums/isf';
import { UntypedFormControl } from '@angular/forms';
import { InternalNotesComponent } from '@shared/internal-notes/components';
import { InternalNote } from '@shared/internal-notes/interfaces';
import { AppService } from '@app/app.service';
import { UtilsHelper } from '@helpers/utils-helper';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { enumUserBetaFlag } from '@data-layer/enums/user';
import { GenericService } from '@services/generic.service';
import { IsfBatchSidebarComponent } from '@shared/isf-mutual/components';
import { enumBatchImportStatus } from '@data-layer/enums/common';
import { enumPaymentState } from '@data-layer/enums/payments';
import { Location } from '@angular/common';

@Component({
  selector: 'isf-list',
  templateUrl: './isf-list.component.html',
  styleUrls: ['./isf-list.component.scss'],
})
export class IsfListComponent implements OnInit, OnDestroy {
  @Input() public showButtonShowMore: boolean = false;
  @Input() public limit: number = 20;
  @Input() public loadNewOnScroll: boolean = true;
  @Input() public scrollContentClass: string;
  @Input() public isShowFilters: boolean;
  @Input() public isShowHeader: boolean = true;
  @Input() public importedFileName: string;
  @Input() public batchItem: BatchImportModel;
  @Output() public showMore: EventEmitter<any> = new EventEmitter<any>();
  @Output() public upload: EventEmitter<File> = new EventEmitter<File>();
  @Output() public uploadError: EventEmitter<void> = new EventEmitter<void>();
  @Output() public closeImport: EventEmitter<void> = new EventEmitter<void>();

  public isfList: PaginatedResultModel<IsfModel> = {
    items: [],
    options: { offset: 0, limit: this.limit },
    total: 0,
  };
  public isShowList: boolean = false;
  public isfCountByStatus: IsfCountByStatusModel[];
  public enumIsfStatusTab: typeof enumIsfStatusTab = enumIsfStatusTab;
  public currentSelectedTab: enumIsfStatusTab = enumIsfStatusTab.ALL;
  public currentSelectedStatuses: enumIsfState[] = [];
  public searchParameterCtrl: UntypedFormControl = new UntypedFormControl();
  public isShowMobileImport: boolean;

  private isLoadingNew: boolean = false;
  private filters: IsfFilterType;
  private companyId: number;
  private draftStates: enumIsfState[] = [enumIsfState.NOT_SENT];
  private correctionsRequiredStates: enumIsfState[] = [
    enumIsfState.REJECTED_CBP,
    enumIsfState.BILL_TYPE_NOT_MATCHED,
    enumIsfState.NO_BILL_ON_FILE,
    enumIsfState.UNRECOVERABLE,
    enumIsfState.NOT_VALID_AT_PROVIDER,
  ];
  private approvedStates: enumIsfState[] = [enumIsfState.APPROVED];
  private isISFSent: boolean;
  private sendISFs$: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private isfRepository: IsfRepository,
    private appLayoutService: AppLayoutService,
    private companiesService: CompaniesService,
    private appService: AppService,
    private genericService: GenericService,
    private batchImportRepository: BatchImportRepository,
    private location: Location,
  ) {}

  public ngOnInit(): void {
    this.companyId = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;

    this.companiesService.companyChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.companyId = this.companiesService.getCompany()?.id;
      this.isfList = {
        items: [],
        options: { offset: 0, limit: this.limit },
        total: 0,
      };
      this.getIsfCountStats();
      this.loadNextIsfs();
    });
    this.appLayoutService.headerActions$
      .pipe(withLatestFrom(this.genericService.isLaptop$), take(1))
      .subscribe(([data, isLaptop]: [IHeaderAction[], boolean]) => {
        data.push({ id: enumHeaderActionId.ISF, routerLink: ['isf', 'disclaimer'], title: 'BUTTON.ADD_NEW_ISF' });
        if (this.hasImportFeature && !isLaptop) {
          data.push({
            id: enumHeaderActionId.ISF_BULK,
            routerLink: [],
            action: this.openISFBulkSidebar.bind(this),
            title: 'BATCH_UPLOAD_ISF',
          });
        }
        this.appLayoutService.headerActions$.next(UtilsHelper.uniqBy<IHeaderAction>(data, 'title'));
      });

    this.appLayoutService.openImportInput$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => {
      this.isShowMobileImport = val;
    });

    this.sendISFs$
      .pipe(
        filter(() => !this.isISFSent),
        switchMap(() => {
          if (!!this.batchItem) {
            return this.sendISFs();
          } else {
            return EMPTY;
          }
        }),
        delay(300),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isISFSent = true;
        this.location.go('/isf');
        this.isfList = {
          items: [],
          options: { offset: 0, limit: this.limit },
          total: 0,
        };
        this.getIsfCountStats();
        this.loadNextIsfs();
      });
  }

  public ngOnDestroy(): void {
    this.appLayoutService.headerActions$.pipe(take(1)).subscribe((data: IHeaderAction[]) => {
      this.appLayoutService.headerActions$.next(
        data.filter((i: IHeaderAction) => ![enumHeaderActionId.ISF, enumHeaderActionId.ISF_BULK].includes(i.id)),
      );
    });
    this.destroy$.next();
  }

  public onShowMore(): void {
    this.showMore.emit();
  }

  public refreshIsfs(filters?: IsfFilterType): void {
    if (!this.companyId) {
      this.isShowList = true;
      this.isfList = { options: null, items: [], total: 0 };
      return;
    }
    if (!!filters) {
      this.filters = SearchHelper.convertFiltersToParams({ ...filters });
    }
    const limit: number = this.isfList.items.length < this.limit ? this.limit : this.isfList.items.length;

    this.isfRepository
      .getIsfList(this.companyId, 0, limit, this.filters)
      .pipe(take(1))
      .subscribe((res: PaginatedResultModel<IsfModel>) => {
        this.isShowList = true;
        this.isfList = res;
        this.sendISFs$.next();
      });
  }

  public loadNextIsfs(): void {
    if (!this.companyId || (this.isfList.items.length > 0 && this.isfList.items.length === this.isfList.total) || this.isLoadingNew) {
      if (!this.companyId) {
        this.isfList = { options: null, items: [], total: 0 };
      }
      this.isShowList = true;
      return;
    }

    this.isLoadingNew = true;
    this.isfRepository
      .getIsfList(this.companyId, this.isfList.items.length, this.limit, this.filters)
      .pipe(
        finalize(() => (this.isLoadingNew = false)),
        take(1),
      )
      .subscribe((res: PaginatedResultModel<IsfModel>) => {
        this.isShowList = true;
        this.isfList.items = [...this.isfList.items, ...res.items];
        this.isfList.options = res.options;
        this.isfList.total = res.total;
        this.sendISFs$.next();
      });
  }

  public getIsfCountStats(): void {
    this.isfRepository
      .getIsfCountStats(this.companyId)
      .pipe(take(1))
      .subscribe((res: IsfCountByStatusModel[]) => {
        this.isfCountByStatus = res;
      });
  }

  public selectTab(status: enumIsfStatusTab): void {
    this.resetData(status);

    switch (status) {
      case enumIsfStatusTab.ALL:
        this.currentSelectedStatuses = [];
        break;
      case enumIsfStatusTab.DRAFTS:
        this.currentSelectedStatuses = this.draftStates;
        break;
      case enumIsfStatusTab.CORRECTIONS_REQUIRED:
        this.currentSelectedStatuses = this.correctionsRequiredStates;
        break;
      case enumIsfStatusTab.APPROVED:
        this.currentSelectedStatuses = this.approvedStates;
        break;
    }

    this.refreshIsfs({ states: this.currentSelectedStatuses, searchParameter: this.searchParameterCtrl.value });
  }

  public searchUpdated(term: string): void {
    this.resetData(enumIsfStatusTab.ALL);
    this.refreshIsfs({ states: this.currentSelectedStatuses, searchParameter: term });
  }

  public resetSearch(): void {
    this.resetData(enumIsfStatusTab.ALL);
    this.searchParameterCtrl.setValue('');
    this.refreshIsfs({ states: this.currentSelectedStatuses });
  }

  public calcItemCount(statuses: enumIsfState[]): number {
    return (this.isfCountByStatus || []).reduce(
      (acc: number, curr: IsfCountByStatusModel) => (statuses.includes(curr.state) ? acc + curr.isfCount : acc),
      0,
    );
  }

  public onEditInternalNotes(isf: IsfModel): void {
    const componentSidebarRef: ComponentRef<InternalNotesComponent> = this.appService.openSidebar(InternalNotesComponent, {
      sidebarHeaderText: !!isf.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT' : 'LABELS.INTERNAL_NOTES_ADD',
      tooltipText: !!isf.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT_DESCRIPTION' : 'LABELS.INTERNAL_NOTES_ADD_DESCRIPTION',
    });
    componentSidebarRef.instance.internalNote = { internalNotes: isf.internalNotes, internalReferenceNumber: isf.internalReferenceNumber };
    componentSidebarRef.instance.isShowDetails = true;
    componentSidebarRef.instance.item = isf;
    componentSidebarRef.instance.isIsf = true;
    componentSidebarRef.instance.saveInternalNotes.pipe(takeUntil(this.destroy$)).subscribe((data: InternalNote) => {
      this.isfRepository
        .updateInternalNote(isf.id, data)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.appLayoutService.internalNotesUpdated$.next();
          this.appService.closeSidebar();

          const index: number = this.isfList.items.findIndex((item: IsfModel) => item.id === isf.id);

          this.isfList.items[index].internalNotes = data.internalNotes;
          this.isfList.items[index].internalReferenceNumber = data.internalReferenceNumber;
        });
    });
  }

  public get hasImportFeature(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.importCustomsFromExcelFeature);
  }

  public get draftsCount(): number {
    return this.calcItemCount(this.draftStates);
  }

  public get correctionsRequiredCount(): number {
    return this.calcItemCount(this.correctionsRequiredStates);
  }

  public get approvedCount(): number {
    return this.calcItemCount(this.approvedStates);
  }

  public get payedBatchItems(): IsfModel[] {
    if (!this.batchItem) {
      return;
    }

    return (this.isfList?.items || []).filter(
      (item: IsfModel) =>
        item.batchId === this.batchItem.id && [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(item.paymentState),
    );
  }

  public get pendingBatchItems(): IsfModel[] {
    if (!this.batchItem) {
      return;
    }

    return (this.isfList?.items || []).filter(
      (item: IsfModel) => item.batchId === this.batchItem.id && [enumPaymentState.PENDING].includes(item.paymentState),
    );
  }

  public get isShowBatchNotificationSuccess(): boolean {
    if (!this.batchItem) {
      return;
    }

    return (
      [enumBatchImportStatus.FINISHED, enumBatchImportStatus.REVIEWED].includes(this.batchItem.status) &&
      !this.batchItem.fileReference &&
      !!this.payedBatchItems.length
    );
  }

  public get isShowBatchNotificationPartial(): boolean {
    if (!this.batchItem) {
      return;
    }

    return (
      [enumBatchImportStatus.FINISHED, enumBatchImportStatus.REVIEWED].includes(this.batchItem.status) &&
      !!this.batchItem.fileReference &&
      !!this.payedBatchItems.length
    );
  }

  public get isShowBatchNotificationPending(): boolean {
    if (!this.batchItem) {
      return;
    }

    return (
      [enumBatchImportStatus.FINISHED, enumBatchImportStatus.REVIEWED].includes(this.batchItem.status) && !!this.pendingBatchItems.length
    );
  }

  public get paymentSuccessCountStr(): string {
    return this.payedBatchItems?.length > 1 ? 'BATCH_AFTER_PAYMENT_STEP_SUCCESS_PLURAL' : 'BATCH_AFTER_PAYMENT_STEP_SUCCESS_SINGLE';
  }

  public get paymentPendingCountStr(): string {
    return this.pendingBatchItems?.length > 1 ? 'BATCH_AFTER_PAYMENT_STEP_PENDING_PLURAL' : 'BATCH_AFTER_PAYMENT_STEP_PENDING_SINGLE';
  }

  private resetData(status: enumIsfStatusTab): void {
    this.currentSelectedTab = status;
    this.currentSelectedStatuses = [];
    this.isfList = { options: null, items: [], total: 0 };
    this.isShowList = false;
  }

  private openISFBulkSidebar(): void {
    const componentSidebarRef: ComponentRef<IsfBatchSidebarComponent> = this.appService.openSidebar(IsfBatchSidebarComponent, {
      sidebarHeaderText: 'ISF_BATCH_UPLOAD',
    });
    componentSidebarRef.instance.onCreated.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.appService.closeSidebar();
    });
  }

  private sendISFs(): Observable<never> {
    const items: string[] = (this.isfList?.items || [])
      ?.filter(
        (isf: IsfModel) =>
          [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(isf.paymentState) && isf.batchId === this.batchItem.id,
      )
      .map((isf: IsfModel) => isf.referenceNumber);

    return items.length ? this.batchImportRepository.sendIsf(items) : NEVER;
  }
}
